export interface ScatterTrendPoint {
  x: number;
  y: number;
}

/**
 * Clip a line segment to axis bounds in data space (Liang-Barsky).
 * Returns clipped endpoints, or null when the segment is fully outside.
 */
export function clipLineSegmentToBounds(
  a: ScatterTrendPoint,
  b: ScatterTrendPoint,
  xMin: number,
  xMax: number,
  yMin: number,
  yMax: number
): [ScatterTrendPoint, ScatterTrendPoint] | null {
  let tEnter = 0;
  let tExit = 1;
  const dx = b.x - a.x;
  const dy = b.y - a.y;

  const clipEdge = (p: number, q: number): boolean => {
    if (p === 0) {
      return q >= 0;
    }
    const t = q / p;
    if (p < 0) {
      if (t > tExit) return false;
      if (t > tEnter) tEnter = t;
    } else {
      if (t < tEnter) return false;
      if (t < tExit) tExit = t;
    }
    return true;
  };

  if (!clipEdge(-dx, a.x - xMin)) return null;
  if (!clipEdge(dx, xMax - a.x)) return null;
  if (!clipEdge(-dy, a.y - yMin)) return null;
  if (!clipEdge(dy, yMax - a.y)) return null;

  if (tEnter > tExit) return null;

  return [
    { x: a.x + tEnter * dx, y: a.y + tEnter * dy },
    { x: a.x + tExit * dx, y: a.y + tExit * dy },
  ];
}

/**
 * Clip a polyline to axis bounds in data space. Returns one or more segments
 * inside [xMin, xMax] x [yMin, yMax], with intersection points at edges.
 */
export function clipPolylineToBounds(
  points: ScatterTrendPoint[],
  xMin: number,
  xMax: number,
  yMin: number,
  yMax: number
): ScatterTrendPoint[][] {
  if (points.length === 0) return [];

  const segments: ScatterTrendPoint[][] = [];
  let current: ScatterTrendPoint[] = [];

  const inside = (p: ScatterTrendPoint) =>
    p.x >= xMin && p.x <= xMax && p.y >= yMin && p.y <= yMax;

  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (inside(p)) {
      if (current.length === 0 && i > 0) {
        const clipped = clipLineSegmentToBounds(points[i - 1], p, xMin, xMax, yMin, yMax);
        if (clipped) current.push(clipped[0]);
      }
      current.push(p);
    } else if (current.length > 0) {
      const clipped = clipLineSegmentToBounds(points[i - 1], p, xMin, xMax, yMin, yMax);
      if (clipped) current.push(clipped[1]);
      segments.push(current);
      current = [];
    }
  }

  if (current.length > 0) segments.push(current);
  return segments;
}
